/* eslint-disable camelcase */
import Resource from '@guestyci/agni';
import { generateCSRFToken } from '@guestyci/agni/csrf';
import { getPermissions } from '@guestyci/ozzy/utils/api';
import { normalizeDPResponse, getIsAllowed } from '@guestyci/ozzy/utils/logic';

import { utils } from 'utils';
import { datadogRum, reportErrorToDataDog } from '../../utils/datadog';

export const changeMFAButtonsText = mfaData => {
  return mfaData.map(item => {
    const element = findElementByText(item.tag, item.text);
    const elementToChange = element?.nextSibling?.firstElementChild;

    if (elementToChange) {
      elementToChange.innerHTML = item.buttonText;
    }

    return element;
  });
};

const findElementByText = (tag, text) => {
  return [...document.querySelectorAll(tag)].find(a =>
    a.textContent.includes(text));
};

export const addedSubTitleForSMSAuthScreen = ({
  smsSubTitleScreenMessage,
  setupSMS,
}) => {
  const smsAuthText = findElementByText('h2', setupSMS);

  if (smsAuthText) {
    const element = document.createElement('p');

    element.innerHTML = smsSubTitleScreenMessage;
    element.classList.add('okta-form-subtitle', 'mb-20');
    smsAuthText.after(element);
    return smsAuthText;
  }

  return '';
};

export const getLayoutApi = () => {
  const isHq = 'hq' === utils.getEnv().dimension;

  const { api } = Resource.create({
    domain: isHq ? 'hq-layout' : 'layout',
  });

  return api;
};

export const getCSRFToken = async () => {
  try {
    await generateCSRFToken({});
  } catch (e) {
    console.error('[CSRF]:', e);
  }

  return Promise.resolve({});
};

export const getSecureAuthCookie = async () => {
  const api = getLayoutApi();
  await getCSRFToken();
  await api.get('/auth');
};

const isTokenExists = () => {
  return Boolean(window.localStorage.getItem('token') ||
      window.localStorage.getItem('okta-token-storage'));
};

export const deleteSecureAuthCookie = async () => {
  const api = getLayoutApi();

  try {
    // Because we don't have access to httpOnly cookies, we can't check if the cookie exists
    // So next best thing is to try to check if the token exists in local storage
    if (isTokenExists()) {
      await api.delete('/auth');
    } else {
      console.log('No token found. Skipping delete cookie request');
    }
  } catch (e) {
    console.error('Failed to delete secure auth cookie', e);
  }
};

// Set of predefined account IDs that should be considered as employee accounts
const EMPLOYEE_ACCOUNT_IDS = new Set([
  '596f6fe706112710005d96ff',
  '563e0b6a08a2710e00057b82',
]);

// Determine if the user is a Guesty employee based on cookies or account ID
// https://github.com/guestyorg/platform-inventory/blob/5eeb8eeb0f406d99388f3571acaee85f1e6d895a/dimensions/guesty/nginx/main%3Aproduction.conf#L465
const isGuestyEmployee = (accountId = null, email = null) => {
  const getEmployeeCookie = () => {
    const cookies = `; ${document.cookie}`;
    const parts = cookies.split('; gempl=');
    if (parts?.length === 2)
      return Boolean(parts
          .pop()
          .split(';')
          .shift());
  };
  try {
    // Check cookie, account ID and email domain
    return Boolean(getEmployeeCookie() ||
        (accountId && EMPLOYEE_ACCOUNT_IDS.has(accountId)) ||
        email?.endsWith('@guesty.com'));
  } catch {
    return false;
  }
};

export const setUserLanguage = (lang = 'en-US') => {
  const authLang = window.localStorage.getItem('auth_lang');
  window.localStorage.setItem('lang', authLang || lang);
};

/**
 * Fetch user data from API
 * @param {Object} options - Options for fetching user data
 * @param {boolean} [options.enrichUser] - Optional flag to enrich user data
 * @returns {Promise<Object|null>} User data or null if failed
 */
export const fetchUserData = async ({ enrichUser = false } = {}) => {
  try {
    const { api } = Resource.create();

    const params = {
      fields: [
        'firstName',
        'lastName',
        'fullName',
        'picture',
        '_id',
        'displayLanguage',
        'account.companyInformation',
        'email',
        'account.name',
        'account.accountCategorization',
        'account.createdAt',
        'account.active',
        'account.freezingFlow',
        'account.billing.noFreezingFlow',
      ].join(' '),
      enrichUser: !!enrichUser,
    };

    const { data: user } = await api.get('/me', { params });
    return user;
  } catch (error) {
    console.error('[authpage]: failed to get user', error);
  }
};

// Returns a stable 0-99 bucket for the given user ID, used as the DataDog RUM
// sample_group attribute to enable user-based (rather than session-based) sampling.
// The same user always lands in the same group across all their sessions.
const getUserSampleGroup = userId =>
  /^[a-f\d]{24}$/i.test(userId)
    ? parseInt(userId.substring(0, 8), 16) % 100
    : null;

export const setDataDogUser = async ({
  isImpersonated = false,
  isInstance = false,
  act = null,
  user,
  isHq = false,
}) => {
  if (!user) {
    return;
  }

  const { api: papApi } = Resource.create({ domain: '/' });
  let roles;
  try {
    const { data: rolesUnfiltered } = await papApi.get(`/pap-http/roles/${user._id}`);
    roles = rolesUnfiltered?.map(({ name, id }) => ({ name, id }));
  } catch (e) {
    console.error('[authpage] Failed to get user roles', e);
    roles = undefined;
  }

  /**
   * This is a temporary solution to determine if the user is in lite or pro package
   * As we don't have a way to get the package type from the backend, and we can't use ozzy PermissionsProvider
   * we are using the DP API to get the package type.
   */
  const ucr = {
    targets: [
      {
        action: 'account:litePackageMonitoring',
        resource: 'accounts',
      },
    ],
  };
  let packageType;
  let isGliteUser;
  try {
    const { data: permissions } = await getPermissions({
      ucr,
      userId: user._id,
    });
    const normalizedPermissions = normalizeDPResponse(permissions || {}, ucr);
    isGliteUser = getIsAllowed(ucr.targets[0].action, normalizedPermissions);
    packageType = isGliteUser ? 'LITE' : 'PRO';
  } catch (e) {
    console.error('[authpage] Failed to get user package type', e);
  }

  const accountId = isHq ? user.orgId : user.accountId;

  datadogRum.setAccount({
    id: accountId,
    name: user.account?.name,
    country: user.account?.companyInformation?.country,
    package: isHq || typeof isGliteUser !== 'boolean' ? undefined : packageType,
    hq: isHq,
  });

  datadogRum.setUser({
    id: user._id,
    account_id: accountId,
    name: user.fullName,
    email: user.email,
    locale: user.displayLanguage || 'en-US',
    impersonated: isImpersonated,
    logged_in: true,
    ...(act && { impersonation_actor: act }),
    instance: isInstance,
    employee: isImpersonated || isGuestyEmployee(user.accountId, user.email),
    roles,
    sample_group: getUserSampleGroup(user._id),
  });
};

/**
 * Clear all DataDog user and account attributes except user.id and account.id
 */
export const clearDataDogUser = () => {
  const currentUserId = datadogRum.getUser()?.id;
  const currentAccountId = datadogRum.getAccount()?.id;

  if (currentUserId) {
    datadogRum.setUser({
      id: currentUserId,
      sample_group: getUserSampleGroup(currentUserId),
    });
  }

  if (currentAccountId) {
    datadogRum.setAccount({
      id: currentAccountId,
    });
  }
};

export const setSecurityScriptDDContext = (
  isLoaded = false,
  scriptSrc = ''
) => {
  const isInternalCDN = scriptSrc.includes('iamg2.guesty.com');
  datadogRum.setGlobalContextProperty('memcyco_script_loaded', isLoaded);
  datadogRum.setGlobalContextProperty('memcyco_internal_cdn', isInternalCDN);
};

export const initAuthDesignDDContext = () => {
  datadogRum.setGlobalContextProperty('auth_page_new_design', false);
};

export const setAuthDesignDDContextEnabled = () => {
  datadogRum.setGlobalContextProperty('auth_page_new_design', true);
};
